import type { FC } from 'react';
import { cn } from '../lib/utils';
import { AssistantMark } from './assistant-mark';

interface ChatPanelTabProps {
  label: string;
  onOpen: () => void;
  /** Count on the tab, e.g. items that need attention. Hidden at zero. */
  badge?: number;
  /** Spoken after the label when `badge` shows, e.g. `need attention`. */
  badgeLabel?: string;
  className?: string;
}

/**
 * The collapsed panel. On wide screens a slim strip down the right edge with
 * the label set vertically; on phones a floating mark above the nav bar.
 */
export const ChatPanelTab: FC<ChatPanelTabProps> = ({
  label,
  onOpen,
  badge = 0,
  badgeLabel = 'new',
  className
}) => (
  <button
    type="button"
    onClick={onOpen}
    aria-label={badge > 0 ? `Open ${label}, ${badge} ${badgeLabel}` : `Open ${label}`}
    className={cn(
      'group/tab flex cursor-pointer flex-col items-center gap-3.5 transition-colors duration-150 ease-standard',
      // phone: a floating disc above the bottom nav
      'max-sm:fixed max-sm:right-4 max-sm:bottom-[5.75rem] max-sm:z-30 max-sm:rounded-full max-sm:shadow-brand',
      // sm+: the edge strip
      'sm:h-full sm:w-14 sm:border-l sm:border-border sm:bg-[var(--z-surface-solid)] sm:py-[22px] sm:hover:bg-[var(--z-surface-raised)]',
      className
    )}
  >
    <span className="relative">
      <AssistantMark className="max-sm:size-12 max-sm:[&_svg]:size-5 sm:size-[30px] sm:[&_svg]:size-4" />
      {badge > 0 ? (
        <span
          aria-hidden="true"
          className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--z-red-600)] px-1 text-[10px] font-bold text-[var(--z-gray-0)] sm:hidden"
        >
          {badge}
        </span>
      ) : null}
    </span>
    <span
      aria-hidden="true"
      className="text-[10.5px] font-semibold tracking-[0.16em] text-[var(--z-text-secondary)] uppercase [writing-mode:vertical-rl] group-hover/tab:text-foreground rotate-180 max-sm:hidden"
    >
      {label}
    </span>
    {badge > 0 ? (
      <span
        aria-hidden="true"
        className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--z-red-600)] px-1 text-[10px] font-bold text-[var(--z-gray-0)] max-sm:hidden"
      >
        {badge}
      </span>
    ) : null}
  </button>
);
